import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import {
    AppBar,
    Box,
    Hidden,
    IconButton,
    Toolbar,
    makeStyles,
    SvgIcon,
    Badge,
} from '@material-ui/core';
import { Menu as MenuIcon } from 'react-feather';
import Logo from 'src/components/Logo';
import { THEMES } from 'src/constants';
import Account from './Account';
import Settings from './Settings';
import useNotification from 'src/hooks/useNotification';
import { SupportIcon } from './SupportIcon';
import Notification from './Notifications';
import Tooltip from '@material-ui/core/Tooltip';
import moment from 'moment';
import useAuth from 'src/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { InternetSignal } from 'src/components/Alerts/InternetSignal';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import { useHistory } from 'react-router-dom';
import { SearchBar } from './SearchBar';

const useStyles = makeStyles((theme) => ({
    root: {
        zIndex: theme.zIndex.drawer + 100,
        ...(theme.name === THEMES.LIGHT
            ? {
                boxShadow: 'none',
                backgroundColor: theme.palette.primary.main,
            }
            : {}),
        ...(theme.name === THEMES.ONE_DARK
            ? {
                backgroundColor: theme.palette.background.default,
            }
            : {}),
        ...(theme.name === THEMES.DARK_REGGAE ||
            theme.name === THEMES.LIGHT_REGGAE
            ? {
                backgroundImage: `linear-gradient(to right, ${theme.palette.primary.main} 20%, ${theme.palette.warning.main} 40%, ${theme.palette.error.main} 60%)`,
            }
            : {}),
        ...(theme.name === THEMES.CHRISTMAS_DARK ||
            theme.name === THEMES.CHRISTMAS_LIGHT
            ? {
                backgroundRepeat: 'repeat',
                backgroundSize: '10%',
                backgroundPosition: '0px -120px',
                backgroundImage:
                    'url("/static/themes/christmas-pattern.png")',
                color: 'white',
            }
            : {}),
        '@media print': {
            display: 'none',
        },
    },
    birthday: {
        backgroundRepeat: 'repeat',
        backgroundSize: '10%',
        backgroundImage:
            'url("https://i.pinimg.com/736x/67/98/8d/67988d494f5bd62945fcd88fff44d80d.jpg")',
        color: 'black',
    },
    deadsDay: {
        backgroundRepeat: 'repeat',
        backgroundPosition: '0px -150px',
        backgroundSize: '12%',
        backgroundImage:
            'url("https://automotive-api.s3.us-east-2.amazonaws.com/5d7a514b5d2c12c7449be042/e945b57f-e721-4a6e-9ea0-b419240176b6/diademuertos.jpeg")',
        color: 'white',
    },
    halloween: {
        backgroundRepeat: 'repeat',
        backgroundSize: '13%',
        backgroundImage:
            'url("https://image.freepik.com/free-vector/hand-drawn-halloween-pattern-with-coffins_52683-44439.jpg")',
        color: 'white',
    },
    christmas: {
        backgroundRepeat: 'repeat',
        backgroundSize: '10%',
        backgroundPosition: '0px -120px',
        backgroundImage: 'url("/static/themes/christmas-pattern.png")',
        color: 'white',
    },
    toolbar: {
        height: 80,
    },
    itemWrapper: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
    },
}));

const TopBar = ({ className, onMobileNavOpen = () => { }, ...rest }) => {
    const classes = useStyles();
    const { notifications } = useNotification();
    const { user } = useAuth();
    const { t } = useTranslation();
    const history = useHistory();

    return (
        <AppBar
            className={clsx({
                [classes.root]: true,
                [classes.deadsDay]: moment().format('DD MM') === '02 11',
                [classes.halloween]: moment().format('DD MM') === '31 10',
                [classes.birthday]:
                    user &&
                    user.birthday &&
                    moment(user.birthday).format('DD MMMM') ===
                    moment().format('DD MMMM'),
                [classes.christmas]: moment().format('DD MM') === '25 12',
            })}
            {...rest}
            elevation={0}
        >
            <Toolbar className={classes.toolbar}>
                <Hidden lgUp>
                    <IconButton color="inherit" onClick={onMobileNavOpen}>
                        <SvgIcon fontSize="small">
                            <MenuIcon />
                        </SvgIcon>
                    </IconButton>
                </Hidden>
                <Hidden mdDown>
                    <RouterLink to="/">
                        <Logo />
                    </RouterLink>
                </Hidden>
                <Box flexGrow={1} />
                <InternetSignal />
                <Box className={classes.itemWrapper}>
                    <Hidden only="xs">
                        <SearchBar />
                    </Hidden>

                    <Tooltip
                        style={{ color: 'white' }}
                        title={t('Tooltips.GoToTickets')}
                    >
                        <IconButton
                            color="inherit"
                            onClick={() => {
                                history.push('/management/tickets');
                            }}
                        >
                            <SupportIcon style={{ fontSize: 25, color: 'white' }} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={t('Tooltips.MyNotifications')}>
                        <Badge
                            overlap="rectangular"
                            badgeContent={notifications?.length || 0}
                            max={99}
                            color="error"
                        >
                            <Notification />
                        </Badge>
                    </Tooltip>

                    <Settings />

                    <Account />
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default TopBar;
